import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import SearchAppBar from './Search';
import BtnComponent from '../buttonComponent/BtnComponent';
import {
  FIND_DISTINCT_SERVICES,
  FIND_SERVICE_NAME_BY_BILLING_CODE,
} from '../../services/PriceTransparencyQueries/PriceTransparencyService';
import { useLazyQuery } from '@apollo/client';
import { SimpleBackdrop } from '../SimpleBackdropComponent/SimpleBackdrop';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import BadgeIcon from '@mui/icons-material/Badge';
import SearchIcon from '@mui/icons-material/Search';
import CustomAutoComplete from './CustomAutoComplete';
import { useSnackbar } from 'notistack';
import { getAffiliateFromDomain } from '../../shared/helper';
import { numberRegex } from '../../shared/constants';

const searchCode = {
  width: '50%',
  minWidth: '440px',
  maxWidth: '500px',
  marginTop: '24px',
};

const container = {
  display: 'flex',
  mt: '33px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.08)',
  //   height: '416px',
  borderRadius: '8px',
  justifyContent: 'space-between',
  background: '#ffffff',
  border: '1px solid #D0D0D0',
};
const searchButton = {
  height: 48,
  width: '100px',
  boxSizing: 'border-box',
  fontSize: 16,
  padding: '12px 24px 12px 24px',
  mt: '40px',
};

const ServicePage = ({
  npiValue,
  setNpiValue,
  serviceValue,
  setServiceName,
  codeValue,
  setCodeValue,
  onSubmit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const serviceDebounceTimer = useRef(null);
  const [localServiceText, setLocalServiceText] = useState('');

  const [fetchServiceNameByBillingCode] = useLazyQuery(
    FIND_SERVICE_NAME_BY_BILLING_CODE,
    {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        onSubmit();
      },
      onError: error => {
        enqueueSnackbar(
          'No service associated with this CPT code, please enter a valid code.',
          { variant: 'error' }
        );
        setCodeValue('');
      },
    }
  );

  const [findAllServices, { data, loading }] = useLazyQuery(
    FIND_DISTINCT_SERVICES,
    {
      fetchPolicy: 'no-cache',
    }
  );
  const findAllService = value => {
    findAllServices({
      variables: {
        distinctServicesInput: {
          network: getAffiliateFromDomain(),
          searchText: value?.toUpperCase(),
        },
      },
    });
  };

  const handleServiceSelection = option => {
    setServiceName(option);
  };

  const handleChangeOnService = value => {
    const text = value || '';
    setLocalServiceText(text);
    setServiceName(text);
    if (serviceDebounceTimer.current) clearTimeout(serviceDebounceTimer.current);
    if (text.trim().length >= 3) {
      serviceDebounceTimer.current = setTimeout(() => {
        findAllService(text.trim());
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (serviceDebounceTimer.current) clearTimeout(serviceDebounceTimer.current);
    };
  }, []);

  const handleCodeSearch = text => {
    setCodeValue(text);
  };

  const handleNpiSearch = text => {
    setNpiValue(text);
  };

  const onSearchClick = () => {
    if (npiValue !== '' && codeValue !== '') {
      fetchServiceNameByBillingCode({
        variables: {
          reqdata: {
            billingCode: codeValue.trim().toUpperCase(),
          },
        },
      });

      if (npiValue.length !== 10 || !numberRegex.test(npiValue)) {
        enqueueSnackbar('Please enter a valid 10 digit NPI code', {
          variant: 'error',
        });
        setNpiValue('');
        return;
      }
    } else if (npiValue !== '') {
      if (npiValue.length !== 10 || !numberRegex.test(npiValue)) {
        enqueueSnackbar('Please enter a valid 10 digit NPI code', {
          variant: 'error',
        });
        setNpiValue('');
        return;
      }
      else {
        onSubmit();
      }
    } else if (codeValue !== '') {
      fetchServiceNameByBillingCode({
        variables: {
          reqdata: {
            billingCode: codeValue.trim().toUpperCase(),
          },
        },
      });
    } else {
      onSubmit();
    }
  };

  return (
    <>
      <Box sx={container}>
        <Box
          sx={{
            display: 'flex',
            margin: '32px',
          }}>
          <Box>
            <Typography
              sx={{
                display: 'flex',
                fontFamily: 'Inter',
                marginBottom: '32px',
              }}
              variant="title">
              {'Search a service'}
            </Typography>

            <CustomAutoComplete
              id={'Service search placeholder'}
              inputPlaceHolder={'Search by service'}
              value={serviceValue}
              clearOnBlur={false}
              onClear={() => {
                setLocalServiceText('');
                setServiceName('');
              }}
              onInputChange={(event, value, reason) => {
                if (reason !== 'reset') {
                  handleChangeOnService(value);
                }
              }}
              onChange={(event, value) => {
                const selected = value || '';
                setLocalServiceText(selected);
                handleServiceSelection(selected);
              }}
              inputIcon={<SearchIcon style={{ color: 'black' }} />}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option.name
              }
              options={localServiceText && data?.findDistinctNameOfServices}
            />

            <SearchAppBar
              searchIcon={<IntegrationInstructionsIcon />}
              placeholder={'Search by CPT code'}
              sx={searchCode}
              value={codeValue}
              addDebounce={false}
              onClearData={() => {
                setCodeValue('');
              }}
              onSearch={handleCodeSearch}
            />

            <SearchAppBar
              searchIcon={<BadgeIcon />}
              placeholder={'Search by provider NPI'}
              sx={searchCode}
              value={npiValue}
              addDebounce={false}
              transformInput={val => val.replace(/[^0-9]/g, '').slice(0, 10)}
              onClearData={() => {
                setNpiValue('');
              }}
              onSearch={handleNpiSearch}
            />

            <BtnComponent
              title="Search"
              sx={searchButton}
              disabled={localServiceText.trim() === '' && codeValue.trim() === '' && npiValue === ''}
              onClick={onSearchClick}></BtnComponent>
          </Box>
        </Box>
        {loading && <SimpleBackdrop open={loading} />}
      </Box>
    </>
  );
};

export default ServicePage;
